"use client"

import {
  useArtifactCanvas,
  hasSearchCriteria,
  searchCriteriaToBriefDraft,
  searchSessionToBriefDraft,
  navigateWithThreadPreservation,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useSearchFormState,
  useSmartAnalysisWorkbench,
  useSmartSearch,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BriefSectionEditAction } from './brief-section-edit-action'
import { Search } from 'lucide-react'
import { useSearchCandidates, useBriefs, type Brief } from '../../hooks/use-briefs'
import { SearchCriteriaForm } from './search-criteria-form'
import { SearchButtons } from './search-buttons'
import { SmartAnalysisResultsShell } from '../search/smart-analysis-results-shell'
import { BriefCanvasForm } from './brief-canvas-form'
import { useNavigateToRecruiterPerson } from '../../hooks/use-navigate-to-recruiter-person'
import { toast } from 'sonner'

export type RecruiterSearchPanelProps = {
  mode: 'talent' | 'brief'
  brief?: Brief
  onEditBrief?: () => void
  /** Skills bridged from Talent browse (`?skills=` on analysis tab). */
  initialSkillsFromBrowse?: string[]
}

export function RecruiterSearchPanel({
  mode,
  brief,
  onEditBrief,
  initialSkillsFromBrowse = [],
}: RecruiterSearchPanelProps) {
  const isTalentMode = mode === 'talent'
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const openPerson = useNavigateToRecruiterPerson({
    origin: isTalentMode ? 'talent' : 'brief',
    briefId: isTalentMode ? null : brief?.id,
  })
  const createBriefCanvasOpenKeyRef = useRef(0)
  const { briefs } = useBriefs()
  const [saveToBriefId, setSaveToBriefId] = useState('')

  const formState = useSearchFormState(
    brief
      ? {
          initialRequiredSkills: brief.skills
            .filter((s) => s.requirement_type === 'required')
            .map((s) => s.skill_name),
          initialPreferredSkills: brief.skills
            .filter((s) => s.requirement_type === 'preferred')
            .map((s) => s.skill_name),
          initialInstructions: brief.instructions || '',
          initialLocation: brief.location || '',
        }
      : undefined,
  )
  const bridgedSkillsKeyRef = useRef('')

  useEffect(() => {
    if (!isTalentMode || initialSkillsFromBrowse.length === 0) return
    const key = initialSkillsFromBrowse.join('\0')
    if (bridgedSkillsKeyRef.current === key) return
    bridgedSkillsKeyRef.current = key
    formState.setRequiredSkills(initialSkillsFromBrowse)
    formState.setPreferredSkills([])
  }, [
    formState.setPreferredSkills,
    formState.setRequiredSkills,
    initialSkillsFromBrowse,
    isTalentMode,
  ])

  const { searchCandidates, isSearching, searchData, searchError } = useSearchCandidates()
  const {
    startSmartSearch,
    stages: smartSearchStages,
    candidates: smartSearchCandidates,
    strategyProgress: smartSearchStrategyProgress,
    parallelOperations: smartSearchParallelOps,
    processingSpeed: smartSearchProcessingSpeed,
    isSearching: isSmartSearching,
    results: smartSearchResults,
    error: smartSearchError,
  } = useSmartSearch()

  const workbench = useSmartAnalysisWorkbench({
    direction: 'candidates',
    context: {
      briefId: isTalentMode ? null : brief?.id,
      standalone: isTalentMode,
    },
    live: {
      direction: 'candidates',
      results: smartSearchResults,
      liveItems: smartSearchCandidates,
      liveSessionId: smartSearchResults?.session_id,
    },
    stages: smartSearchStages,
    isSearching: isSmartSearching,
    searchError: smartSearchError,
    scopeKey: isTalentMode ? 'talent' : brief?.id ?? 'brief',
  })

  const {
    selectedSessionId,
    setSelectedSessionId,
    sessionSummaries,
    selectedSessionDetail,
  } = workbench

  const handleQuickSearch = () => {
    if (!brief) return
    if (formState.requiredSkills.length === 0 && formState.preferredSkills.length === 0) {
      toast.error('Please add at least one skill')
      return
    }

    setSelectedSessionId(null)
    searchCandidates(
      { briefId: brief.id, data: formState.buildSearchData() },
      {
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to search candidates')
        },
      },
    )
  }

  const handleSmartSearch = () => {
    if (formState.requiredSkills.length === 0 && formState.preferredSkills.length === 0) {
      toast.error('Please add at least one skill')
      return
    }

    setSelectedSessionId(null)
    const briefId = isTalentMode ? null : brief?.id ?? null
    try {
      workbench.prepareRun()
      startSmartSearch(briefId, formState.buildSearchData())
    } catch (error: unknown) {
      console.error('[Analysis] Error starting search:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to start analysis')
    }
  }

  const displayResults = workbench.displayResults || searchData
  const displayError = smartSearchError || searchError
  const displayIsSearching = isSmartSearching || isSearching
  const isHydrating =
    workbench.isHydrating && !searchData && !isSearching && !isSmartSearching
  const autoSaveBriefId = isTalentMode ? saveToBriefId || undefined : brief?.id

  const displayCandidates = workbench.displayLiveItems
  const displayStages = workbench.displayStages
  const displaySummary = workbench.displaySummary

  const hasSearchActivity =
    displayIsSearching ||
    displayCandidates.length > 0 ||
    !!displayResults ||
    displayStages.length > 0 ||
    sessionSummaries.length > 0

  const draftCriteriaSource = useMemo(() => {
    if (selectedSessionDetail) {
      return {
        required_skills: selectedSessionDetail.required_skills,
        preferred_skills: selectedSessionDetail.preferred_skills,
        location: selectedSessionDetail.location ?? undefined,
        instructions: selectedSessionDetail.instructions,
        experience_level: selectedSessionDetail.experience_level as
          | 'junior'
          | 'mid'
          | 'senior'
          | 'executive'
          | undefined,
      }
    }

    if (selectedSessionId) {
      const summary = sessionSummaries.find((session) => session.id === selectedSessionId)
      if (summary) {
        return {
          required_skills: summary.required_skills,
          preferred_skills: summary.preferred_skills,
          location: summary.location ?? undefined,
          instructions: summary.instructions,
          experience_level: summary.experience_level as
            | 'junior'
            | 'mid'
            | 'senior'
            | 'executive'
            | undefined,
        }
      }
    }

    return formState.buildSearchData()
  }, [formState, selectedSessionDetail, selectedSessionId, sessionSummaries])

  const canCreateBriefFromSearch = isTalentMode && hasSearchCriteria(draftCriteriaSource)

  const openCreateBriefFromSearchCanvas = useCallback(() => {
    const initialDraft = selectedSessionDetail
      ? searchSessionToBriefDraft(selectedSessionDetail)
      : selectedSessionId
        ? (() => {
            const summary = sessionSummaries.find((session) => session.id === selectedSessionId)
            return summary ? searchSessionToBriefDraft(summary) : searchCriteriaToBriefDraft(formState.buildSearchData())
          })()
        : searchCriteriaToBriefDraft(formState.buildSearchData())

    createBriefCanvasOpenKeyRef.current += 1
    openCanvas({
      title: 'Create brief from search',
      preventAutoFocus: true,
      content: (
        <BriefCanvasForm
          key={`brief-from-search-${createBriefCanvasOpenKeyRef.current}`}
          mode="create"
          initialDraft={initialDraft}
          onClose={closeCanvas}
          onSaved={(created) => {
            closeCanvas()
            navigateWithThreadPreservation(
              navigate,
              `/recruiter/briefs/${created.id}`,
              searchParams,
            )
          }}
        />
      ),
    })
  }, [
    closeCanvas,
    formState,
    navigate,
    openCanvas,
    searchParams,
    selectedSessionDetail,
    selectedSessionId,
    sessionSummaries,
  ])

  return (
    <div className="space-y-6">
      <Card className={!isTalentMode ? 'group/section relative z-0' : undefined}>
        <CardHeader>
          <div className="flex w-full items-start justify-between gap-2">
            <div>
              <CardTitle className={`flex items-center gap-2 ${isTalentMode ? '' : 'text-base'}`}>
                {isTalentMode ? (
                  'Analysis'
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    Search criteria
                  </>
                )}
              </CardTitle>
              <CardDescription>
                {isTalentMode
                  ? 'Browse is a deterministic directory; analysis runs a ranked LLM search across your talent. Use filters on the Talent tab, then run analysis when ready.'
                  : 'Browse talent for deterministic filters, or run analysis for ranked LLM results on this brief.'}
              </CardDescription>
            </div>
            {!isTalentMode && onEditBrief ? (
              <BriefSectionEditAction label="Edit brief criteria" onClick={onEditBrief} />
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <SearchCriteriaForm
            requiredSkills={formState.requiredSkills}
            preferredSkills={formState.preferredSkills}
            instructions={formState.instructions}
            location={formState.location}
            experienceLevel={formState.experienceLevel}
            skillInput={formState.skillInput}
            skillType={formState.skillType}
            onRequiredSkillsChange={formState.setRequiredSkills}
            onPreferredSkillsChange={formState.setPreferredSkills}
            onInstructionsChange={formState.setInstructions}
            onLocationChange={formState.setLocation}
            onExperienceLevelChange={formState.setExperienceLevel}
            onSkillInputChange={formState.setSkillInput}
            onSkillTypeChange={formState.setSkillType}
            onAddSkill={formState.handleAddSkill}
            onRemoveSkill={formState.handleRemoveSkill}
          />
          <SearchButtons
            onQuickSearch={handleQuickSearch}
            onSmartSearch={handleSmartSearch}
            isQuickSearching={isSearching}
            isSmartSearching={isSmartSearching}
            showQuickSearch={!isTalentMode}
          />
          {isTalentMode ? (
            <div className="flex flex-col gap-2 border-t border-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Turn these criteria into a draft brief — add a title and client before saving.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canCreateBriefFromSearch}
                onClick={openCreateBriefFromSearchCanvas}
              >
                Create brief from search
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {isTalentMode && hasSearchActivity && briefs.length > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Optionally choose a brief when saving candidates from results.
          </p>
          <div className="flex items-center gap-2">
            <label htmlFor="save-to-brief" className="text-sm font-medium whitespace-nowrap">
              Save to brief
            </label>
            <Select
              value={saveToBriefId || 'none'}
              onValueChange={(value) => setSaveToBriefId(value === 'none' ? '' : value)}
            >
              <SelectTrigger id="save-to-brief" className="w-[220px]">
                <SelectValue placeholder="Choose on save" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Choose on save</SelectItem>
                {briefs.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}

      <SmartAnalysisResultsShell
        direction="candidates"
        liveItems={displayCandidates}
        results={displayResults ?? null}
        stages={displayStages}
        isSearching={displayIsSearching}
        strategyProgress={smartSearchStrategyProgress}
        parallelOperations={smartSearchParallelOps}
        processingSpeed={smartSearchProcessingSpeed}
        searchError={displayError}
        autoSaveBriefId={autoSaveBriefId}
        isHydrating={isHydrating}
        searchSummary={displaySummary}
        onOpenPerson={openPerson}
      />
    </div>
  )
}
